import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

interface ArticleContent {
  title: string;
  metaDescription: string;
  h1: string;
  intro: string;
  sections: {
    h2: string;
    content: string;
  }[];
  faq: { q: string; a: string }[];
  published: string;
  updated: string;
  readingTime: number;
  breadcrumb: string;
  related: { title: string; path: string }[];
}

interface ArticlePageProps {
  content: ArticleContent;
}

export default function ArticlePage({ content }: ArticlePageProps) {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [toc, setToc] = useState<{ id: string; text: string }[]>([]);

  useEffect(() => {
    const headings = content.sections.map((s, i) => ({
      id: `section-${i}`,
      text: s.h2,
    }));
    setToc(headings);
  }, [content]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-background-100/50 border-b border-background-200/60">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3">
          <nav className="flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              {t("article.breadcrumb.home")}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <span className="text-foreground-700">{content.breadcrumb}</span>
          </nav>
        </div>
      </div>

      {/* Article Header */}
      <article className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-3xl mx-auto">
          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {t("article.published")}: {content.published}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-refresh-line" />
              {t("article.updated")}: {content.updated}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {content.readingTime} {t("article.readingTime")}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line" />
              {t("article.author")}
            </span>
          </div>

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-6 leading-tight">
            {content.h1}
          </h1>

          <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8">
            {content.intro}
          </p>

          {/* Table of Contents */}
          {toc.length > 0 && (
            <div className="bg-background-100/50 border border-background-200/60 rounded-lg p-4 md:p-5 mb-8">
              <h3 className="text-sm font-semibold text-foreground-800 mb-3">
                {t("article.tableOfContents")}
              </h3>
              <ul className="space-y-1.5">
                {toc.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                    >
                      {item.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Content Sections */}
          <div className="space-y-8 md:space-y-10">
            {content.sections.map((section, i) => (
              <section key={i} id={`section-${i}`} className="scroll-mt-20">
                <h2 className="text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                  {section.h2}
                </h2>
                <div className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-4">
                  {section.content.split("\n\n").map((paragraph, pi) => (
                    <p key={pi}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* FAQ */}
          {content.faq.length > 0 && (
            <div className="mt-12 pt-8 border-t border-background-200/60">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
                FAQ
              </h2>
              <div className="space-y-3">
                {content.faq.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-background-100/50 border border-background-200/60 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-4 text-left"
                    >
                      <span className="text-sm font-medium text-foreground-800 pr-4">
                        {item.q}
                      </span>
                      <i
                        className={`ri-${openFaq === idx ? "subtract" : "add"}-line text-foreground-400 shrink-0`}
                      />
                    </button>
                    {openFaq === idx && (
                      <div className="px-4 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {item.a}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA Box */}
          <div className="mt-12 bg-primary-50/50 border border-primary-200/60 rounded-xl p-6 md:p-8 text-center">
            <h3 className="text-lg font-semibold text-foreground-900 mb-2">
              {t("cta.main.title")}
            </h3>
            <p className="text-sm text-foreground-600 mb-5">
              {t("cta.main.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors"
              >
                {t("article.cta")}
                <i className="ri-arrow-right-line" />
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-background-50 border border-background-200 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-100 transition-colors"
              >
                {t("article.ctaSecondary")}
              </a>
            </div>
          </div>

          {/* Related Articles */}
          {content.related.length > 0 && (
            <div className="mt-12 pt-8 border-t border-background-200/60">
              <h3 className="text-lg font-semibold text-foreground-900 mb-4">
                {t("article.related")}
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {content.related.map((rel, i) => (
                  <Link
                    key={i}
                    to={rel.path}
                    className="flex items-center gap-3 p-3 bg-background-100/50 border border-background-200/60 rounded-lg hover:border-primary-300 hover:bg-primary-50/30 transition-all"
                  >
                    <i className="ri-article-line text-primary-500" />
                    <span className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors">
                      {rel.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Footer meta */}
          <div className="mt-12 pt-6 border-t border-background-200/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-foreground-500">
            <span>
              {t("article.lastReviewed")}: {content.updated}
            </span>
            <span className="italic">
              {t("disclaimer")}
            </span>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}